import {
    Bitcoin,
    Wallet,
    TrendingUp,
    Search,
    BarChart3,
    Rocket,
    CheckCircle2,
    Map,
    Zap,
    Users,
    Activity,
    Target,
    Shield,
    Globe,
    Grid,
    Cpu,
    Briefcase,
    DollarSign,
    Gift,
    Clock,
    Scale
} from 'lucide-react';

export const cryptoContent = [
    {
        title: "AI-Powered Revenue Acceleration Engine",
        tagline: "Convert Speculative Volume into Recurring Revenue Streams.",
        description: "Crypto businesses struggle to translate users, volume, and community into sustainable revenue. This engine connects demand, activation, monetization, and retention into a single AI-driven revenue layer—optimizing token economics, transaction flows, subscriptions, and protocol fees.",
        icon: Bitcoin,
        metrics: [
            { label: "Improvement in revenue velocity", value: "30–50%" },
            { label: "Monetization per active wallet", value: "20-40%" },
            { label: "Revenue leakage", value: "Reduced" },
            { label: "Income predictability", value: "High" }
        ],
        portfolioCards: [
            { title: "Revenue Flow Intelligence", icon: Activity },
            { title: "Token & Fee Monetization", icon: TrendingUp },
            { title: "Expansion & Retention Signals", icon: Users },
            { title: "Revenue Forecasting", icon: BarChart3 }
        ],
        verticalTabs: [
            { title: "Centralized Exchange (CEX)", content: "AI optimizes fee structures and forecasts revenue based on market data. Impact: 30-50% revenue velocity increase.", icon: Activity },
            { title: "Decentralized Exchange (DEX)", content: "Real-time on-chain analysis to enhance protocol fees and liquidity incentives. Impact: 20-40% higher monetization per swap.", icon: Grid },
            { title: "Wallet Services", content: "Expansion signals detect upsell opportunities like premium features. Impact: 20-40% LTV boost per wallet.", icon: Wallet },
            { title: "DeFi Lending/Borrowing", content: "AI forecasts defaults and optimizes rates using blockchain data. Impact: 30-50% revenue uplift from spreads.", icon: TrendingUp },
            { title: "NFT Marketplace", content: "Monetization dashboards track secondary sales and automate fee collection. Impact: 20-40% increase in recurring royalties.", icon: Shop },
            { title: "ICO/IDO/IEO", content: "Forecasting tools model token economics for long-term fees. Impact: 30-50% better post-launch revenue.", icon: Rocket },
            { title: "Mining/Staking Operations", content: "AI optimizes node rewards and fee distribution. Impact: 20-40% more predictable rewards.", icon: Cpu },
            { title: "Crypto Payment Gateways", content: "Dynamic fee adjustments and revenue dashboards. Impact: 30-50% velocity boost; 20% higher merchant LTV.", icon: DollarSign },
            { title: "Yield Farming / Aggregators", content: "Integration for fee optimization across protocols. Impact: 20-40% higher yields; reduced leakage.", icon: Activity }
        ],
        icp: {
            title: "Who Needs Revenue Acceleration?",
            audience: [
                "DeFi Protocols with high TVL but low revenue capture",
                "L1/L2 Foundations seeking ecosystem sustainability",
                "Web3 Gaming Studios needing player monetization",
                "Exchanges (CEX/DEX) maximizing trading fees"
            ],
            outcome: "Transformation from speculative volume to predictable, recurring revenue."
        }
    },
    {
        title: "AI-Powered Crypto Demand Discovery Engine",
        tagline: "Uncover Real Adoption Intent Before Speculation Hits.",
        description: "Crypto demand is noisy and speculative. This engine separates real adoption intent from hype by analyzing on-chain signals, off-chain behavior, community sentiment, and ecosystem activity—surfacing demand before it becomes obvious.",
        icon: Search,
        metrics: [
            { label: "Increase in high-intent signals", value: "15X–25X" },
            { label: "Acquisition quality", value: "30-50%" },
            { label: "Go-to-market alignment", value: "Faster" },
            { label: "Wasted acquisition spend", value: "-40%" }
        ],
        portfolioCards: [
            { title: "On-Chain Intent Intelligence", icon: Globe },
            { title: "Community Signal Analysis", icon: Users },
            { title: "Wallet-Level Demand Scoring", icon: Wallet },
            { title: "Market Timing Intelligence", icon: Activity }
        ],
        verticalTabs: [
            { title: "Centralized Exchange (CEX)", content: "Wallet scoring identifies high-volume traders. Impact: 15X intent signals; 30-50% better acquisition quality.", icon: BarChart3 },
            { title: "Decentralized Exchange (DEX)", content: "On-chain signal analysis for genuine LP intent. Impact: 30-50% improved LP acquisition.", icon: users },
            { title: "Wallet Services", content: "Behavior analysis scores demand for features like staking. Impact: 15X high-intent users; 40% spend reduction.", icon: Wallet },
            { title: "DeFi Lending/Borrowing", content: "Ecosystem activity models predict participation. Impact: 30-50% quality uplift; higher utilization.", icon: Activity },
            { title: "NFT Marketplace", content: "Sentiment tools uncover collector intent. Impact: 15X signals; 40% less wasted spend.", icon: Shop },
            { title: "ICO/IDO/IEO", content: "Demand scoring for pre-launch targeting. Impact: 30-50% better raise quality; faster funding.", icon: Rocket },
            { title: "Mining/Staking Operations", content: "Market timing for reward seekers. Impact: 15X intent; 40% acquisition efficiency.", icon: Cpu },
            { title: "Crypto Payment Gateways", content: "Off-chain behavior analysis for integration leads. Impact: 30-50% quality; reduced CAC.", icon: DollarSign },
            { title: "Yield Farming / Aggregators", content: "Activity scoring filters genuine yield seekers. Impact: 15X signals; 40% better retention.", icon: Activity }
        ],
        icp: {
            title: "Who Needs Demand Discovery?",
            audience: [
                "New Token Launches & ICOs/IDOs",
                "NFT Collections seeking genuine collectors",
                "Protocols entering new market segments",
                "Marketing leads struggling with attribution"
            ],
            outcome: "Identify and capture high-quality users before competitors do."
        }
    },
    {
        title: "AI-Powered Performance Revenue Engine",
        tagline: "Optimize Every Campaign for Maximum Revenue Contribution.",
        description: "Performance in crypto is not about clicks—it’s about activation, liquidity, and monetization. This engine continuously optimizes campaigns, incentives, and channels based on revenue contribution, not vanity metrics.",
        icon: BarChart3,
        metrics: [
            { label: "Improvement in ROAS", value: "25–45%" },
            { label: "Cost per activated user", value: "-20-35%" },
            { label: "Transaction conversion", value: "Higher" },
            { label: "Incentive wastage", value: "-40%" }
        ],
        portfolioCards: [
            { title: "Channel-to-Revenue Attribution", icon: Grid },
            { title: "Incentive & Reward Optimization", icon: Gift },
            { title: "Conversion Path Intelligence", icon: Map },
            { title: "ROI-Based Spend Allocation", icon: DollarSign }
        ],
        verticalTabs: [
            { title: "Centralized Exchange (CEX)", content: "Attribution tracks trading fees; optimizes incentives. Impact: 25-45% ROAS boost.", icon: BarChart3 },
            { title: "Decentralized Exchange (DEX)", content: "Funnel detection for protocol fees; anti-fraud tweaks. Impact: 40% wastage reduction.", icon: Grid },
            { title: "Wallet Services", content: "ROI allocation for feature upsells. Impact: 25-45% better ROAS; 20-35% activation cost drop.", icon: Wallet },
            { title: "DeFi Lending/Borrowing", content: "Conversion intelligence for interest revenue. Impact: 40% less wastage; higher contributions.", icon: TrendingUp },
            { title: "NFT Marketplace", content: "Breakpoint detection for sales funnels. Impact: 25-45% ROAS; 20-35% user cost reduction.", icon: Shop },
            { title: "ICO/IDO/IEO", content: "Incentive optimization for investor paths. Impact: 40% wastage cut; better funding ROI.", icon: Rocket },
            { title: "Mining/Staking Operations", content: "Attribution for node engagements. Impact: 25-45% uplift; 20-35% cost savings.", icon: Cpu },
            { title: "Crypto Payment Gateways", content: "Spend allocation for transaction fees. Impact: 40% reduction in wastage.", icon: DollarSign },
            { title: "Yield Farming / Aggregators", content: "Optimization for aggregator fees. Impact: 25-45% ROAS; 20-35% lower costs.", icon: Activity }
        ],
        icp: {
            title: "Who Needs Performance Optimization?",
            audience: [
                "Growth Teams at Centralized Exchanges",
                "DeFi projects running liquidity mining programs",
                "Projects with high marketing spend but low retention",
                "Teams fighting 'airdrop farmers' and bots"
            ],
            outcome: "Ensure every token and dollar spent drives real user activation."
        }
    },
    {
        title: "AI Growth Acceleration Engine",
        tagline: "Scale Winning Strategies with AI-Led Experimentation.",
        description: "Crypto growth is often driven by intuition and rapid experiments. This engine replaces guesswork with AI-led experimentation, identifying scalable growth levers across acquisition, activation, liquidity, and retention.",
        icon: Rocket,
        metrics: [
            { label: "Faster experimentation cycles", value: "5X–10X" },
            { label: "Activation-to-retention conv.", value: "30-50%" },
            { label: "Cost of failed experiments", value: "-40%" },
            { label: "Scaling winning strategies", value: "Faster" }
        ],
        portfolioCards: [
            { title: "Growth Experiment Intelligence", icon: Activity },
            { title: "Funnel Breakpoint Detection", icon: Target },
            { title: "Liquidity & Adoption Levers", icon: TrendingUp },
            { title: "Scale Readiness Scoring", icon: CheckCircle2 }
        ],
        verticalTabs: [
            { title: "Centralized Exchange (CEX)", content: "A/B testing for trading incentives; scale scoring. Impact: 5X faster cycles; 30-50% retention boost.", icon: BarChart3 },
            { title: "Decentralized Exchange (DEX)", content: "Experiment intelligence for pools. Impact: 40% lower failure costs; exponential TVL.", icon: Grid },
            { title: "Wallet Services", content: "Predictive funnels for user loops. Impact: 5X speed; 30-50% conversion uplift.", icon: Wallet },
            { title: "DeFi Lending/Borrowing", content: "Levers for yield adjustments. Impact: 40% cost reduction; better scaling.", icon: TrendingUp },
            { title: "NFT Marketplace", content: "A/B for community incentives. Impact: 5X cycles; 30-50% retention.", icon: Shop },
            { title: "ICO/IDO/IEO", content: "Scale readiness for token strategies. Impact: 40% lower costs; faster ecosystem build.", icon: Rocket },
            { title: "Mining/Staking Operations", content: "Adoption levers for rewards. Impact: 5X speed; 30-50% growth.", icon: Cpu },
            { title: "Crypto Payment Gateways", content: "Funnel optimization for integrations. Impact: 40% reduction; exponential users.", icon: DollarSign },
            { title: "Yield Farming / Aggregators", content: "Experiment frameworks for yields. Impact: 5X faster; 30-50% retention.", icon: Activity }
        ],
        icp: {
            title: "Who Needs Growth Acceleration?",
            audience: [
                "Post-Launch Projects hitting a growth plateau",
                "Scale-up DAOs managing growth treasuries",
                "Ecosystem funds supporting portfolio project",
                "Founders looking for repeatable growth loops"
            ],
            outcome: "Systematic, data-driven scaling beyond the initial hype cycle."
        }
    },
    {
        title: "AI Market Validation Intelligence",
        tagline: "Validate Demand and Fit Before Your Token Launch.",
        description: "Many crypto products fail due to weak market validation. This engine validates problem–solution fit, demand readiness, and pricing logic before major investments in protocol development or token launches.",
        icon: CheckCircle2,
        metrics: [
            { label: "Faster validation cycles", value: "50%" },
            { label: "Go-to-market risk", value: "-30-40%" },
            { label: "Launch success rates", value: "50-70%" },
            { label: "Product–market fit", value: "Better" }
        ],
        portfolioCards: [
            { title: "Market Signal Intelligence", icon: Activity },
            { title: "Demand Readiness Scoring", icon: Target },
            { title: "Competitive Analysis", icon: Search },
            { title: "Launch Timing Prediction", icon: Clock }
        ],
        verticalTabs: [
            { title: "Centralized Exchange (CEX)", content: "Demand scoring for new listings; timing predictions. Impact: 50% faster cycles; 30-40% risk cut.", icon: BarChart3 },
            { title: "Decentralized Exchange (DEX)", content: "Competitive analysis for liquidity models. Impact: 50-70% success rate; lower burn.", icon: Grid },
            { title: "Wallet Services", content: "On-chain surveys for user needs. Impact: 50% cycle reduction; 30-40% risk drop.", icon: Wallet },
            { title: "DeFi Lending/Borrowing", content: "Simulations for rate fits. Impact: 50-70% launch success; efficient capital.", icon: TrendingUp },
            { title: "NFT Marketplace", content: "Market signals for trends. Impact: 50% faster; 30-40% risk reduction.", icon: Shop },
            { title: "ICO/IDO/IEO", content: "Readiness scoring for investors. Impact: 50-70% success; confident funding.", icon: Rocket },
            { title: "Mining/Staking Operations", content: "Predictive timing for networks. Impact: 50% reduction; 30-40% lower burn.", icon: Cpu },
            { title: "Crypto Payment Gateways", content: "Risk assessments for integrations. Impact: 50-70% success; efficient use.", icon: DollarSign },
            { title: "Yield Farming / Aggregators", content: "Competitive simulations. Impact: 50% cycles; 30-40% risk cut.", icon: Activity }
        ],
        icp: {
            title: "Who Needs Market Validation?",
            audience: [
                "Pre-Token Founders & Builders",
                "Venture Studios incubating new protocols",
                "Corporate Web3 divisions entering the space",
                "Investors conducting due diligence"
            ],
            outcome: "Launch with confidence, knowing there is real market demand."
        }
    },
    {
        title: "AI Customer Journey Intelligence",
        tagline: "Map and Monetize Every Wallet Interaction.",
        description: "Crypto journeys span discovery, onboarding, activation, usage, governance, and expansion. This engine maps end-to-end wallet and user journeys, identifying friction, churn risk, and monetization opportunities.",
        icon: Map,
        metrics: [
            { label: "Activation and retention", value: "25-45%" },
            { label: "Churn after onboarding", value: "-30%" },
            { label: "Lifetime value per wallet", value: "20-40%" },
            { label: "Ecosystem engagement", value: "Improved" }
        ],
        portfolioCards: [
            { title: "Wallet Journey Mapping", icon: Map },
            { title: "Churn & Retention Prediction", icon: Users },
            { title: "Expansion Opportunity Detection", icon: TrendingUp },
            { title: "Lifecycle Intelligence", icon: Activity }
        ],
        verticalTabs: [
            { title: "Centralized Exchange (CEX)", content: "Journey mapping for personalized nudges. Impact: 25-45% retention; 30% churn drop.", icon: BarChart3 },
            { title: "Decentralized Exchange (DEX)", content: "Churn prediction for liquidity paths. Impact: 20-40% LTV boost; loyal users.", icon: Grid },
            { title: "Wallet Services", content: "Lifecycle intelligence for expansions. Impact: 25-45% rates; 30% less churn.", icon: Wallet },
            { title: "DeFi Lending/Borrowing", content: "Retention prediction for yields. Impact: 20-40% LTV; recurring engagement.", icon: TrendingUp },
            { title: "NFT Marketplace", content: "Opportunity detection for collections. Impact: 25-45% retention; 30% churn reduction.", icon: Shop },
            { title: "ICO/IDO/IEO", content: "Monetization nudges for ecosystems. Impact: 20-40% LTV; loyal holders.", icon: Rocket },
            { title: "Mining/Staking Operations", content: "Journey personalization for rewards. Impact: 25-45% activation; 30% churn drop.", icon: Cpu },
            { title: "Crypto Payment Gateways", content: "Expansion detection for transactions. Impact: 20-40% LTV; better engagement.", icon: DollarSign },
            { title: "Yield Farming / Aggregators", content: "Prediction for yield paths. Impact: 25-45% retention; 30% less churn.", icon: Activity }
        ],
        icp: {
            title: "Who Needs Journey Intelligence?",
            audience: [
                "Wallet Providers & Interface designs",
                "dApps with complex user flows",
                "Ecosystems focused on long-term retention",
                "Product Managers optimizing conversion"
            ],
            outcome: "Seamless user experiences that retain users and liquidity."
        }
    },
    {
        title: "AI Startup MVP Acceleration Engine",
        tagline: "From Idea to MVP and GTM in Record Time.",
        description: "Crypto startups must validate fast without burning capital. This engine accelerates idea validation, MVP design, early traction, and GTM readiness, ensuring founders build what the market will adopt.",
        icon: Zap,
        metrics: [
            { label: "Reduction in MVP time-to-market", value: "40–60%" },
            { label: "User and partner validation", value: "30-50%" },
            { label: "Burn rate pre-launch", value: "-25%" },
            { label: "Investor readiness", value: "Higher" }
        ],
        portfolioCards: [
            { title: "Idea-to-MVP Intelligence", icon: Zap },
            { title: "Early Demand Signals", icon: Activity },
            { title: "GTM Readiness Scoring", icon: CheckCircle2 },
            { title: "Investor Narrative Validation", icon: Briefcase }
        ],
        verticalTabs: [
            { title: "Centralized Exchange (CEX)", content: "Prototyping for new tools; GTM scoring. Impact: 40-60% faster TTM; 30-50% validation speed.", icon: BarChart3 },
            { title: "Decentralized Exchange (DEX)", content: "Demand signals for early MVPs. Impact: 25% lower burn; faster entry.", icon: Grid },
            { title: "Wallet Services", content: "Narrative validation for funding. Impact: 40-60% TTM reduction; better readiness.", icon: Wallet },
            { title: "DeFi Lending/Borrowing", content: "Feedback loops for prototypes. Impact: 30-50% speed; 25% burn cut.", icon: TrendingUp },
            { title: "NFT Marketplace", content: "User networks for validation. Impact: 40-60% faster; accelerated funding.", icon: Shop },
            { title: "ICO/IDO/IEO", content: "Investor tools for narratives. Impact: 30-50% validation; 25% lower burn.", icon: Rocket },
            { title: "Mining/Staking Operations", content: "Partner matching for MVPs. Impact: 40-60% TTM; faster market.", icon: Cpu },
            { title: "Crypto Payment Gateways", content: "Early signals for GTM. Impact: 30-50% speed; 25% burn reduction.", icon: DollarSign },
            { title: "Yield Farming/Aggregators", content: "Prototyping with AI loops. Impact: 40-60% faster; better entry.", icon: Activity }
        ],
        icp: {
            title: "Who Needs MVP Acceleration?",
            audience: [
                "Early-Stage Crypto Founders",
                "Hackathon Winners transitioning to startups",
                "Builders seeking their first users",
                "Accelerators supporting new cohorts"
            ],
            outcome: "Build the right product faster and secure initial traction."
        }
    }
];

// Helper to provide a Shop icon fallback since it's not in lucide-react default export in some versions
// Actually, 'Shop' isn't in lucide-react, I will use 'ShoppingBag' or similar. 
// Wait, I will use simple icons from lucide-react that are available.
// 'Store' or 'ShoppingBag'. I will use 'Store'.
import { Store as Shop, Users as users } from 'lucide-react'; 
